import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Copy } from "lucide-react";

function nextMonth(period) {
  try {
    const [y, m] = period.split("-").map(Number);
    return new Date(y, m, 1).toISOString().slice(0, 7);
  } catch {
    return new Date().toISOString().slice(0, 7);
  }
}

export default function DuplicateReportButton({ report, big = false }) {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(() => nextMonth(report.period));
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const openDialog = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPeriod(nextMonth(report.period));
    setOpen(true);
  };

  const submit = async () => {
    if (!period) return;
    setSending(true);
    try {
      const { data } = await api.post(`/expense-reports/${report.id}/duplicate`, { period });
      toast.success(`Brouillon ${data.report.number} créé pour ${period}. Pensez à joindre les justificatifs du mois avant l'envoi.`);
      setOpen(false);
      navigate(`/nouvelle-note/${data.report.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button onClick={openDialog}
        className={big
          ? "btn-secondary-lg !h-12 !px-5 !text-base"
          : "inline-flex items-center gap-2 px-4 h-11 rounded-xl border-2 border-[#0369a1] text-[#0369a1] font-semibold text-sm hover:bg-sky-50"}
        data-testid={`duplicate-report-${report.id}`}>
        <Copy className="h-4 w-4" aria-hidden="true" strokeWidth={2.5} /> Dupliquer
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Dupliquer {report.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <p className="text-lg text-slate-700">
              Un nouveau <strong>brouillon</strong> sera créé avec les mêmes dépenses (dates décalées sur la
              période choisie). Les justificatifs ne sont pas copiés : joignez ceux du mois avant l'envoi.
            </p>
            <div>
              <Label htmlFor="dup-period" className="label-lg">Période de la nouvelle note</Label>
              <Input id="dup-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                className="input-lg" data-testid="duplicate-period-input" />
            </div>
            <button onClick={submit} disabled={sending || !period} className="btn-primary-lg w-full" data-testid="duplicate-confirm-button">
              {sending ? "Duplication…" : "Créer le brouillon"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
